import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { Calendar, Cloud } from 'lucide-react';
import LegalSection from '../components/settings/LegalSection';
import QrSection from '../components/settings/QrSection';
import NotificationsSection from '../components/settings/NotificationsSection';
import SubscriptionSection from '../components/settings/SubscriptionSection';
import TemplatesSection from '../components/settings/TemplatesSection';
import TeamSection from '../components/settings/TeamSection';

/**
 * Thin orchestrator. All large sections live in /components/settings/*.
 * This file owns the practice form state + the simple section toggles
 * (industry, profile, slot activation, calendars, CSV import, language).
 */
export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { user, practice, setPractice } = useAuth();
  const [form, setForm] = useState({});
  const [industries, setIndustries] = useState([]);
  const [calendar, setCalendar] = useState({ google: null, outlook: null, ical: null });
  const [icalUrl, setIcalUrl] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get('/api/industries').then((r) => setIndustries(r.data.industries || [])); }, []);
  useEffect(() => {
    if (!practice) return;
    setForm({
      name: practice.name || '',
      description: practice.description || '',
      address: practice.address || '',
      logo_url: practice.logo_url || '',
      practice_type: practice.practice_type || 'wahlarzt',
      hourly_rate: practice.hourly_rate || 100,
      public_enabled: practice.public_enabled !== false,
      auto_waitlist_activation: practice.auto_waitlist_activation !== false,
      pull_forward_enabled: practice.pull_forward_enabled !== false,
      max_pull_forward_chain: practice.max_pull_forward_chain || 3,
      pull_forward_window_days: practice.pull_forward_window_days || 30,
      token_ttl_min: practice.token_ttl_min || 30,
      smart_timing_enabled: practice.smart_timing_enabled !== false,
      predictive_enabled: practice.predictive_enabled !== false,
      google_review_enabled: practice.google_review_enabled === true,
      google_review_url: practice.google_review_url || '',
      google_review_delay_hours: practice.google_review_delay_hours || 24,
      impressum: practice.impressum || '',
      datenschutz: practice.datenschutz || '',
    });
    api.get('/api/calendar/status').then((r) => setCalendar(r.data));
    setIcalUrl(practice.ical_feed_url || '');
  }, [practice]);

  const currentLang = i18n.language?.startsWith('en') ? 'en' : 'de';
  async function changeLang(lang) {
    if (lang === currentLang) return;
    await i18n.changeLanguage(lang); localStorage.setItem('studioops_lang', lang);
    if (practice) try { const r = await api.patch(`/api/practices/${practice._id}/language`, { language: lang }); setPractice(r.data.practice); } catch (_) {}
    toast.success(t('settings.languageSaved'));
  }
  function setF(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  async function save() {
    if (!practice) return;
    setSaving(true);
    try { const r = await api.patch(`/api/practices/${practice._id}`, form); setPractice(r.data.practice); toast.success(t('settings.saveSuccess')); }
    catch (_) { toast.error(t('errors.unknown')); }
    finally { setSaving(false); }
  }

  async function connectCal(kind) {
    try {
      await api.post(`/api/calendar/${kind}/connect`);
      const r = await api.get('/api/calendar/status'); setCalendar(r.data);
      toast.success(`${kind === 'google' ? 'Google' : 'Outlook'} verbunden (Demo-Modus)`);
    } catch (_) { toast.error(t('errors.unknown')); }
  }
  async function disconnectCal(kind) {
    await api.post(`/api/calendar/${kind}/disconnect`);
    const r = await api.get('/api/calendar/status'); setCalendar(r.data);
  }
  async function saveIcal() {
    if (!icalUrl) return;
    try {
      const r = await api.post('/api/calendar/ical', { url: icalUrl });
      toast.success(`Sync OK · ${r.data.synced} Termine`);
      const s = await api.get('/api/calendar/status'); setCalendar(s.data);
    } catch (_) { toast.error('Sync fehlgeschlagen'); }
  }
  async function syncIcal() {
    const r = await api.post('/api/calendar/ical/sync');
    toast.success(`Sync OK · ${r.data.synced || 0} Termine`);
    const s = await api.get('/api/calendar/status'); setCalendar(s.data);
  }

  if (!practice) return <SuperadminSettings user={user} t={t} currentLang={currentLang} changeLang={changeLang}/>;

  return (
    <div data-testid="settings-page">
      <h1 className="text-2xl font-bold tracking-tight">{t('settings.title')}</h1>
      <p className="mt-1 text-sm text-text-secondary">{t('settings.subtitle')}</p>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Section title={t('settings.sections.industryPrice')}>
          <Field label={t('settings.industry.label')}>
            <select value={form.practice_type || 'wahlarzt'} onChange={(e) => setF('practice_type', e.target.value)} className="so-input" data-testid="set-practice-type">
              {industries.map((i) => <option key={i.id} value={i.id}>{i.label_de}</option>)}
            </select>
            <p className="mt-1 text-xs text-text-secondary">{t('settings.industry.hint')}</p>
          </Field>
          <Field label={t('settings.industry.hourlyRate')}>
            <input type="number" min="0" value={form.hourly_rate || ''} onChange={(e) => setF('hourly_rate', Number(e.target.value))} className="so-input" data-testid="set-hourly-rate"/>
          </Field>
        </Section>

        <Section title={t('settings.sections.practiceProfile')}>
          <Field label={t('settings.profile.name')}><input className="so-input" value={form.name || ''} onChange={(e) => setF('name', e.target.value)} data-testid="set-name"/></Field>
          <Field label={t('settings.profile.description')}><textarea rows={2} className="so-input" value={form.description || ''} onChange={(e) => setF('description', e.target.value)} data-testid="set-description"/></Field>
          <Field label={t('settings.profile.address')}><input className="so-input" value={form.address || ''} onChange={(e) => setF('address', e.target.value)} data-testid="set-address"/></Field>
        </Section>

        <QrSection practice={practice} form={form} setF={setF} t={t}/>

        <Section title={t('settings.sections.autoActivation')}>
          <label className="flex items-start gap-3 mb-3"><input type="checkbox" className="mt-0.5" checked={!!form.auto_waitlist_activation} onChange={(e) => setF('auto_waitlist_activation', e.target.checked)} data-testid="set-auto-activation"/><span className="text-sm">{t('settings.activation.autoSlot')}</span></label>
          <label className="flex items-start gap-3 mb-3"><input type="checkbox" className="mt-0.5" checked={!!form.pull_forward_enabled} onChange={(e) => setF('pull_forward_enabled', e.target.checked)} data-testid="set-pull-forward"/><span className="text-sm">{t('settings.activation.pullForward')}</span></label>
          <Field label={t('settings.activation.windowDays', { days: form.pull_forward_window_days })}>
            <select value={form.pull_forward_window_days || 30} onChange={(e) => setF('pull_forward_window_days', Number(e.target.value))} className="so-input" data-testid="set-pf-window">
              <option value={7}>7 Tage</option><option value={14}>14 Tage</option><option value={30}>30 Tage</option>
            </select>
          </Field>
          <Field label={t('settings.activation.maxChain')}>
            <input type="number" min={1} max={5} value={form.max_pull_forward_chain || 3} onChange={(e) => setF('max_pull_forward_chain', Number(e.target.value))} className="so-input" data-testid="set-pf-chain"/>
          </Field>
          <Field label={t('settings.activation.tokenTtl')}>
            <select value={form.token_ttl_min || 30} onChange={(e) => setF('token_ttl_min', Number(e.target.value))} className="so-input" data-testid="set-token-ttl">
              <option value={15}>15</option><option value={30}>30</option><option value={60}>60</option>
            </select>
          </Field>
        </Section>

        <NotificationsSection form={form} setF={setF} t={t}/>

        <Section title={t('settings.sections.calendars')}>
          <div className="space-y-3">
            <Connector icon={Cloud} label="Google Calendar" connected={!!calendar.google} email={calendar.google?.account_email} onConnect={() => connectCal('google')} onDisconnect={() => disconnectCal('google')} testid="cal-google" t={t}/>
            <Connector icon={Cloud} label="Microsoft Outlook" connected={!!calendar.outlook} email={calendar.outlook?.account_email} onConnect={() => connectCal('outlook')} onDisconnect={() => disconnectCal('outlook')} testid="cal-outlook" t={t}/>
            <div className="rounded-md border border-border p-3">
              <div className="flex items-center justify-between mb-2"><div className="flex items-center gap-2"><Calendar size={14}/><span className="text-sm font-medium">iCal Feed</span></div>
                {calendar.ical && <button onClick={syncIcal} className="text-xs text-accent hover:underline">{t('settings.calendars.icalSyncNow')}</button>}
              </div>
              <input value={icalUrl} onChange={(e) => setIcalUrl(e.target.value)} placeholder="https://kalender.example.com/feed.ics" className="so-input font-mono text-xs" data-testid="cal-ical-url"/>
              <div className="mt-2 flex items-center gap-2">
                <button onClick={saveIcal} className="so-btn so-btn-secondary text-xs" data-testid="cal-ical-save">{t('settings.calendars.icalSave')}</button>
                {calendar.ical?.last_sync_at && <span className="text-[11px] text-text-secondary">Letzter Sync: {new Date(calendar.ical.last_sync_at).toLocaleString('de-AT')} · {calendar.ical.last_sync_count} Termine</span>}
              </div>
              <p className="mt-2 text-[11px] text-text-secondary">{t('settings.calendars.icalHint')}</p>
            </div>
          </div>
        </Section>

        <Section title={t('settings.sections.csvImport')}>
          <CsvImport t={t}/>
        </Section>

        <Section title={t('settings.sections.googleReviews')}>
          <label className="flex items-start gap-3 mb-3"><input type="checkbox" className="mt-0.5" checked={!!form.google_review_enabled} onChange={(e) => setF('google_review_enabled', e.target.checked)} data-testid="set-google-reviews"/><span className="text-sm">{t('settings.reviews.optIn')}</span></label>
          <Field label={t('settings.reviews.urlLabel')}><input value={form.google_review_url || ''} onChange={(e) => setF('google_review_url', e.target.value)} placeholder="https://g.page/r/…" className="so-input font-mono text-xs" data-testid="set-review-url"/></Field>
          <Field label={t('settings.reviews.delayLabel')}>
            <select value={form.google_review_delay_hours || 24} onChange={(e) => setF('google_review_delay_hours', Number(e.target.value))} className="so-input" data-testid="set-review-delay">
              <option value={2}>2 Stunden</option><option value={24}>24 Stunden</option><option value={48}>48 Stunden</option>
            </select>
          </Field>
        </Section>

        <LegalSection form={form} setF={setF} t={t}/>

        <SubscriptionSection practice={practice} className="lg:col-span-2"/>

        <TeamSection className="lg:col-span-2"/>

        <TemplatesSection className="lg:col-span-2"/>

        <Section title={t('settings.languageTitle')} className="lg:col-span-2">
          <div className="grid grid-cols-2 gap-3 max-w-sm">
            <button onClick={() => changeLang('de')} className={`rounded-md border px-4 py-3 text-left ${currentLang === 'de' ? 'border-accent ring-2 ring-accent/30 bg-accent/5' : 'border-border bg-white'}`} data-testid="settings-lang-de"><p className="text-sm font-semibold">Deutsch</p></button>
            <button onClick={() => changeLang('en')} className={`rounded-md border px-4 py-3 text-left ${currentLang === 'en' ? 'border-accent ring-2 ring-accent/30 bg-accent/5' : 'border-border bg-white'}`} data-testid="settings-lang-en"><p className="text-sm font-semibold">English</p></button>
          </div>
        </Section>
      </div>

      <div className="flex items-center justify-end mt-6">
        <button onClick={save} disabled={saving} className="so-btn so-btn-primary text-base px-6" data-testid="settings-save">{saving ? '…' : t('settings.saveAll')}</button>
      </div>
    </div>
  );
}

function Section({ title, children, className = '' }) { return <div className={`so-card p-5 ${className}`}><h2 className="text-sm font-semibold mb-4">{title}</h2>{children}</div>; }
function Field({ label, hint, children }) {
  return (
    <div className="mb-3">
      <label className="so-label">{label}</label>
      {hint && <p className="text-[11px] text-text-secondary mb-1.5 -mt-0.5">{hint}</p>}
      {children}
    </div>
  );
}
function Connector({ icon: Icon, label, connected, email, onConnect, onDisconnect, testid, t }) {
  return (
    <div className="rounded-md border border-border p-3 flex items-center justify-between" data-testid={testid}>
      <div className="flex items-center gap-3">
        <Icon size={16} className="text-accent"/>
        <div>
          <p className="text-sm font-medium">{label}</p>
          {connected && email && <p className="text-xs text-text-secondary font-mono">{email}</p>}
        </div>
      </div>
      {connected ? (
        <div className="flex items-center gap-2"><span className="so-pill so-pill-success">{t('settings.calendars.connected')}</span><button onClick={onDisconnect} className="text-xs text-danger hover:underline">{t('settings.calendars.disconnect')}</button></div>
      ) : (
        <button onClick={onConnect} className="so-btn so-btn-secondary text-xs">{t('settings.calendars.connect')}</button>
      )}
    </div>
  );
}

function CsvImport({ t }) {
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);

  async function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    const tok = localStorage.getItem('studioops_access_token');
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/import/csv/preview`, {
      method: 'POST', headers: { Authorization: `Bearer ${tok}` }, body: fd,
    });
    const d = await r.json();
    if (d.error) { toast.error(d.error); return; }
    setPreview(d); setMapping({});
  }
  async function doImport() {
    if (!preview) return;
    if (!mapping.patient_first_name || !mapping.patient_last_name || !mapping.starts_at_date || !mapping.starts_at_time) {
      toast.error(t('settings.csv.requiredFields')); return;
    }
    setImporting(true);
    try {
      const r = await api.post('/api/import/csv', { csv: preview.raw, mapping });
      toast.success(`Importiert: ${r.data.imported}, übersprungen: ${r.data.skipped}`);
      setPreview(null);
    } catch (e) { toast.error(t('settings.csv.importFailed')); }
    finally { setImporting(false); }
  }
  return (
    <div data-testid="csv-import">
      <input type="file" accept=".csv,text/csv" onChange={onFile} className="text-sm" data-testid="csv-file"/>
      {preview && (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-text-secondary">{preview.total_rows} Zeilen erkannt, Spalten: {preview.headers.join(', ')}</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              ['patient_first_name', t('settings.csv.firstName')],
              ['patient_last_name', t('settings.csv.lastName')],
              ['starts_at_date', t('settings.csv.date')],
              ['starts_at_time', t('settings.csv.time')],
              ['patient_phone', t('settings.csv.phone')],
              ['duration_min', t('settings.csv.duration')],
              ['note', t('settings.csv.note')],
            ].map(([k, label]) => (
              <label key={k} className="text-xs">
                <span className="block mb-1 text-text-secondary">{label}</span>
                <select value={mapping[k] || ''} onChange={(e) => setMapping({ ...mapping, [k]: e.target.value })} className="so-input py-2 text-xs" data-testid={`csv-map-${k}`}>
                  <option value="">—</option>
                  {preview.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </label>
            ))}
          </div>
          <button onClick={doImport} disabled={importing} className="so-btn so-btn-primary text-sm mt-2" data-testid="csv-import-btn">{importing ? '…' : t('settings.csv.import')}</button>
        </div>
      )}
    </div>
  );
}

function SuperadminSettings({ user, t, currentLang, changeLang }) {
  const [stats, setStats] = useState(null);
  const [locations, setLocations] = useState([]);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    api.get('/api/platform/stats').then((r) => setStats(r.data)).catch(() => {});
    api.get('/api/locations').then((r) => setLocations(r.data.locations || [])).catch(() => {});
    loadAudit();
  }, []);
  async function loadAudit() {
    setAuditLoading(true);
    try {
      const r = await api.get('/api/audit-log?limit=50');
      setAuditRows(r.data.rows || []);
    } catch (_) { /* swallow */ }
    finally { setAuditLoading(false); }
  }
  function pickPractice(loc) {
    api.defaults.headers['X-Practice-Id'] = loc._id;
    localStorage.setItem('studioops_active_practice', loc._id);
    window.location.reload();
  }
  const fmtTs = (d) => d ? new Date(d).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  const actionLabel = {
    staff_invited: t('audit.actions.staff_invited'),
    invite_accepted: t('audit.actions.invite_accepted'),
    invite_revoked: t('audit.actions.invite_revoked'),
    staff_removed: t('audit.actions.staff_removed'),
    locations_updated: t('audit.actions.locations_updated'),
    role_updated: t('audit.actions.role_updated'),
    dsgvo_export: t('audit.actions.dsgvo_export'),
  };

  return (
    <div data-testid="settings-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t('settings.title')}</h1>
        <p className="mt-1 text-sm text-text-secondary">
          {t('settings.superadminSubtitle')} · <span className="font-mono">{user?.email}</span>
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="platform-stats">
        <Stat label={t('settings.platform.practicesTotal')} value={stats?.practices_total ?? '—'} color="#1B3A6B"/>
        <Stat label={t('settings.platform.practicesActive')} value={stats?.practices_active ?? '—'} color="#16A34A"/>
        <Stat label={t('settings.platform.twilioProvisioned')} value={stats?.practices_provisioned ?? '—'} color="#2B7FD4"/>
        <Stat label={t('settings.platform.locationGroups')} value={stats?.location_groups ?? '—'} color="#9333EA"/>
        <Stat label={t('settings.platform.usersTotal')} value={stats?.users_total ?? '—'} color="#1B3A6B"/>
        <Stat label={t('settings.platform.waitlistTotal')} value={stats?.waitlist_patients_total ?? '—'} color="#2B7FD4"/>
        <Stat label={t('settings.platform.appointmentsTotal')} value={stats?.appointments_total ?? '—'} color="#D97706"/>
        <Stat label={t('settings.platform.messagesTotal')} value={stats?.messages_total ?? '—'} color="#16A34A"/>
      </div>

      <div className="so-card p-5" data-testid="platform-providers">
        <h2 className="text-sm font-semibold mb-3">{t('settings.platform.providersTitle')}</h2>
        <div className="grid grid-cols-3 gap-3 text-sm">
          <ProviderTile label="Twilio" value={stats?.twilio_provider || 'mock'}/>
          <ProviderTile label="Kalender" value={stats?.calendar_provider || 'mock'}/>
          <ProviderTile label="Environment" value={stats?.node_env || 'development'}/>
        </div>
        <p className="mt-3 text-xs text-text-secondary">{t('settings.platform.providersHint')}</p>
      </div>

      <div className="so-card p-5" data-testid="platform-practices">
        <h2 className="text-sm font-semibold mb-3">{t('settings.platform.practicesManageTitle')}</h2>
        <p className="text-xs text-text-secondary mb-4">{t('settings.platform.practicesManageHint')}</p>
        {locations.length === 0 ? (
          <p className="text-text-secondary text-sm">{t('settings.platform.practicesEmpty')}</p>
        ) : (
          <ul className="grid sm:grid-cols-2 gap-3">
            {locations.map((l) => {
              const qrPng = `${process.env.REACT_APP_BACKEND_URL}/api/qr/${l.slug}.png`;
              const qrSvg = `${process.env.REACT_APP_BACKEND_URL}/api/qr/${l.slug}.svg`;
              return (
                <li key={l._id} className="rounded-md border border-border bg-white p-4 hover:border-accent transition" data-testid={`platform-card-${l.slug}`}>
                  <div className="flex gap-4">
                    <img src={qrPng} alt={`QR ${l.name}`} className="w-24 h-24 border border-border rounded shrink-0" data-testid={`platform-qr-${l.slug}`}/>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{l.location_label || l.name}</p>
                      <p className="text-xs text-text-secondary font-mono truncate">{l.slug}</p>
                      <p className="text-xs text-text-secondary font-mono truncate">{l.twilio_whatsapp_number || '—'}</p>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <button onClick={() => pickPractice(l)} className="so-btn so-btn-secondary text-xs" data-testid={`platform-pick-${l.slug}`}>{t('settings.platform.openPractice')}</button>
                        <a href={qrPng} download={`qr-${l.slug}.png`} className="so-btn so-btn-secondary text-xs" data-testid={`platform-qr-png-${l.slug}`}>PNG</a>
                        <a href={qrSvg} download={`qr-${l.slug}.svg`} className="so-btn so-btn-secondary text-xs" data-testid={`platform-qr-svg-${l.slug}`}>SVG</a>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="so-card p-5" data-testid="audit-log-section">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold">{t('audit.title')}</h2>
            <p className="text-xs text-text-secondary mt-0.5">{t('audit.subtitle')}</p>
          </div>
          <button onClick={loadAudit} className="so-btn so-btn-secondary text-xs" data-testid="audit-refresh-btn">{auditLoading ? '…' : t('audit.refresh')}</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="text-left text-[11px] uppercase text-text-secondary border-b border-border">
              <tr>
                <th className="py-2 pr-2">{t('audit.table.when')}</th>
                <th className="py-2 pr-2">{t('audit.table.action')}</th>
                <th className="py-2 pr-2">{t('audit.table.performedBy')}</th>
                <th className="py-2 pr-2">{t('audit.table.target')}</th>
                <th className="py-2 pr-2">{t('audit.table.practice')}</th>
                <th className="py-2 pr-2">{t('audit.table.details')}</th>
              </tr>
            </thead>
            <tbody>
              {auditRows.length === 0 && (
                <tr><td colSpan={6} className="py-4 text-center text-text-secondary text-xs">{t('audit.empty')}</td></tr>
              )}
              {auditRows.map((row) => (
                <tr key={row._id} className="border-b border-border last:border-b-0" data-testid={`audit-row-${row._id}`}>
                  <td className="py-2 pr-2 text-xs text-text-secondary whitespace-nowrap">{fmtTs(row.created_at)}</td>
                  <td className="py-2 pr-2"><span className="text-xs font-medium">{actionLabel[row.action] || row.action}</span></td>
                  <td className="py-2 pr-2 text-xs">{row.performed_by?.email || '—'}</td>
                  <td className="py-2 pr-2 text-xs">{row.target_user?.email || '—'}</td>
                  <td className="py-2 pr-2 text-xs text-text-secondary font-mono">{row.practice_id?.slice(0, 8) || '—'}</td>
                  <td className="py-2 pr-2 text-[10px] text-text-secondary font-mono max-w-[260px] truncate" title={JSON.stringify(row.details || {})}>
                    {row.details ? JSON.stringify(row.details).slice(0, 80) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Section title={t('settings.languageTitle')}>
        <p className="mb-3 text-xs text-text-secondary">{t('settings.languageHint')}</p>
        <div className="grid grid-cols-2 gap-3 max-w-sm">
          <button onClick={() => changeLang('de')} className={`rounded-md border px-4 py-3 text-left ${currentLang === 'de' ? 'border-accent ring-2 ring-accent/30 bg-accent/5' : 'border-border bg-white'}`} data-testid="settings-lang-de">
            <p className="text-sm font-semibold">Deutsch</p>
          </button>
          <button onClick={() => changeLang('en')} className={`rounded-md border px-4 py-3 text-left ${currentLang === 'en' ? 'border-accent ring-2 ring-accent/30 bg-accent/5' : 'border-border bg-white'}`} data-testid="settings-lang-en">
            <p className="text-sm font-semibold">English</p>
          </button>
        </div>
      </Section>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="so-card p-4">
      <p className="text-[11px] uppercase tracking-wide text-text-secondary font-semibold">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums" style={{ color }}>{value}</p>
    </div>
  );
}
function ProviderTile({ label, value }) {
  const isReal = String(value).toLowerCase() === 'real' || String(value).toLowerCase() === 'production';
  return (
    <div className="rounded-md border border-border bg-background/40 p-3">
      <p className="text-[11px] uppercase tracking-wide text-text-secondary font-semibold">{label}</p>
      <p className={`mt-1 text-sm font-mono ${isReal ? 'text-success' : 'text-warning'}`}>{value}</p>
    </div>
  );
}
